import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  db, collection, query, where, onSnapshot
} from '../../firebase/config';
import {
  Package, ShoppingBag, ChevronRight, ChevronDown, MapPin,
  Truck, Store, Check, Clock, MessageCircle, Zap, X,
  User, CreditCard
} from 'lucide-react';

const OrdersPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');   // 'active' | 'past'
  const [expandedOrder, setExpandedOrder] = useState(null);

  // ---------- Load client's orders (real-time) ----------
  useEffect(() => {
    if (!user?.uid) return;

    const q = query(
      collection(db, 'orders'),
      where('clientId', '==', user.uid)
    );

    const unsub = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => {
        const aT = a.createdAt?.toMillis?.() ?? new Date(a.createdAt || 0).getTime();
        const bT = b.createdAt?.toMillis?.() ?? new Date(b.createdAt || 0).getTime();
        return bT - aT;
      });
      setOrders(data);
      setLoading(false);
    }, (err) => {
      console.error('Orders snapshot:', err);
      setLoading(false);
    });

    return () => unsub();
  }, [user?.uid]);

  // ---------- Split active vs past ----------
  const { active, past } = useMemo(() => {
    const ACTIVE_STATUSES = ['pending', 'preparing', 'shipped', 'ready_for_collection'];
    const PAST_STATUSES = ['delivered', 'collected', 'cancelled'];

    const activeList = orders.filter(o =>
      ACTIVE_STATUSES.includes(o.fulfillmentStatus)
    );
    const pastList = orders.filter(o =>
      PAST_STATUSES.includes(o.fulfillmentStatus) ||
      !o.fulfillmentStatus // legacy orders default to past
    );

    return { active: activeList, past: pastList };
  }, [orders]);

  const displayedOrders = activeTab === 'active' ? active : past;

  // ---------- Helpers ----------
  const formatPrice = (n) => `R${Number(n || 0).toFixed(2)}`;

  const formatDate = (ts) => {
    if (!ts) return '';
    try {
      const d = ts.toDate ? ts.toDate() : new Date(ts);
      return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return '';
    }
  };

  const getItemImage = (item) => {
    if (item.image) return item.image;
    if (Array.isArray(item.media) && item.media.length > 0) {
      const first = item.media[0];
      if (first.type === 'image') return first.url;
    }
    if (item.mediaLegacy?.url) return item.mediaLegacy.url;
    return null;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending': return { bg: 'bg-amber-50', text: 'text-amber-600', label: 'New' };
      case 'preparing': return { bg: 'bg-blue-50', text: 'text-blue-600', label: 'Preparing' };
      case 'shipped': return { bg: 'bg-purple-50', text: 'text-purple-600', label: 'Shipped' };
      case 'ready_for_collection': return { bg: 'bg-green-50', text: 'text-green-600', label: 'Ready for pickup' };
      case 'delivered': return { bg: 'bg-green-50', text: 'text-green-600', label: 'Delivered' };
      case 'collected': return { bg: 'bg-green-50', text: 'text-green-600', label: 'Collected' };
      case 'cancelled': return { bg: 'bg-red-50', text: 'text-red-500', label: 'Cancelled' };
      default: return { bg: 'bg-gray-50', text: 'text-gray-500', label: status || 'Processing' };
    }
  };

  const getDeliveryLabel = (order) => {
    if (order.deliveryMethod === 'standard') return `Standard · ${order.deliveryEstimate || '3-5 days'}`;
    if (order.deliveryMethod === 'express') return `Express · ${order.deliveryEstimate || '1-2 days'}`;
    if (order.deliveryMethod === 'collect') return 'Collect in person';
    return 'Delivery';
  };

  const getDeliveryIcon = (order) => {
    if (order.deliveryMethod === 'collect') return <Store size={13} />;
    if (order.deliveryMethod === 'express') return <Zap size={13} />;
    return <Truck size={13} />;
  };

  const handleMessageVendor = (order) => {
    if (!order.vendorId) return;
    navigate(`/dashboard/chat?with=${order.vendorId}`);
  };

  // ---------- Loading ----------
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-[#EFA9B6] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // ---------- Empty (no orders at all) ----------
  if (orders.length === 0) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-serif font-bold text-gray-900">Your Orders</h1>
          <p className="text-gray-500 mt-1">Track your purchases and deliveries</p>
        </div>

        <div className="text-center py-20 bg-white rounded-3xl border border-gray-50">
          <div className="w-24 h-24 bg-[#FFF5F6] rounded-full flex items-center justify-center mx-auto mb-4">
            <Package size={40} className="text-[#EFA9B6]" />
          </div>
          <h3 className="text-xl font-serif font-bold text-gray-900 mb-2">No orders yet</h3>
          <p className="text-gray-400 text-sm mb-6 max-w-sm mx-auto">
            When you buy products from our vendors, they'll show up here.
          </p>
          <button
            onClick={() => navigate('/dashboard/shop')}
            className="px-6 py-3 bg-[#010101] text-white rounded-full text-sm font-bold hover:bg-black/90 transition-colors"
          >
            Browse Shop
          </button>
        </div>
      </div>
    );
  }

  // ---------- Main render ----------
  return (
    <div className="max-w-2xl mx-auto pb-20">

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-serif font-bold text-gray-900">Your Orders</h1>
        <p className="text-gray-500 mt-1">
          {active.length} active · {past.length} past
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-white border border-gray-100 rounded-full mb-5">
        <button
          onClick={() => setActiveTab('active')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full text-sm font-bold transition-colors ${
            activeTab === 'active'
              ? 'bg-[#010101] text-white'
              : 'text-gray-500 hover:bg-[#FCF8F9]'
          }`}
        >
          Active
          {active.length > 0 && (
            <span className={`min-w-[20px] h-5 px-1.5 rounded-full text-[10px] flex items-center justify-center ${
              activeTab === 'active' ? 'bg-white text-[#010101]' : 'bg-[#FFF5F6] text-[#EFA9B6]'
            }`}>
              {active.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('past')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full text-sm font-bold transition-colors ${
            activeTab === 'past'
              ? 'bg-[#010101] text-white'
              : 'text-gray-500 hover:bg-[#FCF8F9]'
          }`}
        >
          Past
          {past.length > 0 && (
            <span className={`min-w-[20px] h-5 px-1.5 rounded-full text-[10px] flex items-center justify-center ${
              activeTab === 'past' ? 'bg-white text-[#010101]' : 'bg-[#FFF5F6] text-[#EFA9B6]'
            }`}>
              {past.length}
            </span>
          )}
        </button>
      </div>

      {/* Empty state for this tab */}
      {displayedOrders.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-3xl border border-gray-50">
          <div className="w-16 h-16 bg-[#F4EEFF] rounded-full flex items-center justify-center mx-auto mb-3">
            {activeTab === 'active' ? (
              <Clock size={24} className="text-[#AF8BD4]" />
            ) : (
              <Check size={24} className="text-[#AF8BD4]" />
            )}
          </div>
          <p className="text-gray-400 text-sm">
            {activeTab === 'active' ? 'No active orders' : 'No past orders yet'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {displayedOrders.map((order) => {
            const badge = getStatusBadge(order.fulfillmentStatus);
            const isExpanded = expandedOrder === order.id;
            const items = order.items || [];
            const firstItem = items[0];
            const moreCount = items.length - 1;
            const isCollect = order.deliveryMethod === 'collect';
            const totalQty = items.reduce((s, i) => s + (i.quantity || 1), 0);

            return (
              <div
                key={order.id}
                className="bg-white rounded-3xl border border-gray-100 hover:border-[#EFA9B6] transition-colors overflow-hidden"
              >

                {/* Summary row */}
                <button
                  onClick={() => setExpandedOrder(isExpanded ? null : order.id)}
                  className="w-full p-4 flex items-center gap-3 text-left"
                >
                  {/* First item thumbnail */}
                  <div className="relative w-14 h-14 rounded-2xl bg-[#FCF8F9] flex items-center justify-center overflow-hidden shrink-0">
                    {firstItem && getItemImage(firstItem) ? (
                      <img src={getItemImage(firstItem)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <ShoppingBag size={20} className="text-gray-300" />
                    )}
                    {moreCount > 0 && (
                      <span className="absolute bottom-1 right-1 bg-black/70 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">
                        +{moreCount}
                      </span>
                    )}
                  </div>

                  {/* Middle */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${badge.bg} ${badge.text}`}>
                        {badge.label}
                      </span>
                      <span className="text-[10px] text-gray-400 font-mono">
                        #{order.id.slice(-6).toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm font-bold text-gray-900 truncate">
                      {order.vendorName || 'Skytone'}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {totalQty} item{totalQty === 1 ? '' : 's'} · {formatDate(order.createdAt)}
                    </p>
                  </div>

                  {/* Right */}
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="font-bold text-gray-900 text-sm">
                      {formatPrice(order.total)}
                    </span>
                    <ChevronDown
                      size={16}
                      className={`text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    />
                  </div>
                </button>

                {/* Expanded details */}
                {isExpanded && (
                  <div className="border-t border-gray-50 p-4 space-y-4">

                    {/* Items */}
                    <div>
                      <p className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] mb-2">
                        Items
                      </p>
                      <div className="space-y-3">
                        {items.map((item, idx) => {
                          const img = getItemImage(item);
                          return (
                            <div key={idx} className="flex items-center gap-3">
                              <div className="w-12 h-12 rounded-xl bg-[#FCF8F9] flex items-center justify-center overflow-hidden shrink-0">
                                {img ? (
                                  <img src={img} alt={item.name} className="w-full h-full object-cover" />
                                ) : (
                                  <ShoppingBag size={16} className="text-gray-300" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-bold text-gray-900 truncate">{item.name}</p>
                                {item.brand && (
                                  <p className="text-xs text-gray-400 truncate">{item.brand}</p>
                                )}
                                <p className="text-xs text-gray-400">Qty: {item.quantity}</p>
                              </div>
                              <span className="font-bold text-sm text-gray-900 shrink-0">
                                {formatPrice((item.price || 0) * (item.quantity || 1))}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Delivery */}
                    <div className="bg-[#FCF8F9] rounded-2xl p-3">
                      <p className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] mb-2 flex items-center gap-1.5">
                        {getDeliveryIcon(order)}
                        {isCollect ? 'Collection' : 'Delivery'}
                      </p>
                      <p className="text-sm font-bold text-gray-900">
                        {getDeliveryLabel(order)}
                      </p>

                      {isCollect && order.collectAddress && (
                        <div className="text-xs text-gray-600 mt-1.5 flex items-start gap-1.5">
                          <MapPin size={12} className="mt-0.5 shrink-0 text-[#AF8BD4]" />
                          <span>{order.collectAddress}</span>
                        </div>
                      )}

                      {!isCollect && order.deliveryAddress && (
                        <div className="text-xs text-gray-600 mt-1.5">
                          <p>{order.deliveryAddress.fullName}</p>
                          <p>{order.deliveryAddress.line1 || order.deliveryAddress.address}</p>
                          <p>
                            {order.deliveryAddress.city}
                            {order.deliveryAddress.postalCode ? `, ${order.deliveryAddress.postalCode}` : ''}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Payment summary */}
                    <div className="bg-[#FCF8F9] rounded-2xl p-3 space-y-1.5 text-sm">
                      <div className="flex justify-between text-gray-500">
                        <span>Subtotal</span>
                        <span>{formatPrice(order.subtotal)}</span>
                      </div>
                      <div className="flex justify-between text-gray-500">
                        <span>Delivery</span>
                        <span>
                          {Number(order.deliveryFee ?? order.shipping ?? 0) === 0 ? (
                            <span className="text-green-600 font-medium">Free</span>
                          ) : (
                            formatPrice(order.deliveryFee ?? order.shipping)
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between font-bold text-gray-900 pt-1.5 border-t border-gray-100">
                        <span>Total paid</span>
                        <span>{formatPrice(order.total)}</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 pt-1">
                      {order.vendorId && (
                        <button
                          onClick={() => handleMessageVendor(order)}
                          className="flex-1 py-2.5 rounded-full bg-[#FCF8F9] text-gray-700 font-bold text-sm hover:bg-gray-100 transition-colors flex items-center justify-center gap-2 border border-gray-100"
                        >
                          <MessageCircle size={15} />
                          Message Vendor
                        </button>
                      )}
                      <button
                        onClick={() => navigate(`/order-confirmation/${order.id}`)}
                        className="flex-1 py-2.5 rounded-full bg-[#010101] text-white font-bold text-sm hover:bg-black/90 transition-colors flex items-center justify-center gap-2"
                      >
                        View Receipt
                        <ChevronRight size={15} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Footer hint */}
      {orders.length > 0 && (
        <p className="text-center text-[11px] text-gray-400 mt-8 leading-relaxed">
          Need help with an order? Tap the order to see details,<br />
          or message the vendor directly from your order.
        </p>
      )}
    </div>
  );
};

export default OrdersPage;